import { DestroyRef, Injectable, inject } from "@angular/core";

/** What can hold the auto-hide timer: the pointer over the element or the focus inside it. */
export type AutoHidePauseSource = "pointer" | "focus";

/**
 * Hides an element after a delay, paused while it is hovered or focused (WCAG 2.2.1).
 * Releasing the last pause starts a fresh full period.
 *
 * Component-scoped: the timer is cleared when the component is destroyed.
 */
@Injectable()
export class AutoHideTimerService {
  private readonly pauses = new Set<AutoHidePauseSource>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: { durationMs: number; onExpire: () => void } | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /**
   * Starts a fresh period that calls `onExpire` once elapsed.
   * While paused, the period only starts when every pause is released.
   */
  public start(durationMs: number, onExpire: () => void): void {
    this.pending = { durationMs, onExpire };
    this.schedule();
  }

  /** Cancels the period and forgets the pauses. */
  public stop(): void {
    this.clearTimer();
    this.pending = null;
    this.pauses.clear();
  }

  /** Holds the timer until the same source resumes it. */
  public pause(source: AutoHidePauseSource): void {
    this.pauses.add(source);
    this.clearTimer();
  }

  /** Releases a pause; a fresh period starts when no pause is left. */
  public resume(source: AutoHidePauseSource): void {
    this.pauses.delete(source);
    this.schedule();
  }

  private schedule(): void {
    this.clearTimer();
    if (this.pending === null || this.pauses.size > 0) {
      return;
    }
    const { durationMs, onExpire } = this.pending;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.pending = null;
      onExpire();
    }, durationMs);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
